
import React, { useState } from "react";
import { Star, StarHalf } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface BookingCompletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: BookingCompletionData) => void;
  booking: any;
  loading?: boolean;
  initialAmount?: number;
  currencySymbol?: string;
}

export interface BookingCompletionData {
  rating: number;
  comment: string;
  paymentAmount: number;
}

export const BookingCompletionDialog: React.FC<BookingCompletionDialogProps> = ({
  isOpen,
  onClose,
  onComplete,
  booking,
  loading = false,
  initialAmount = 0,
  currencySymbol = "Ksh"
}) => {
  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState<string>("");
  const [paymentAmount, setPaymentAmount] = useState<string>(initialAmount.toString());
  const [error, setError] = useState<string | null>(null);

  const handleRatingChange = (newRating: number) => {
    setRating(newRating);
  };

  const handleSubmit = () => {
    // Validate payment amount
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid payment amount");
      return;
    }

    // Submit the completion data
    onComplete({
      rating,
      comment,
      paymentAmount: amount
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !loading && !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Complete Booking</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div>
            <Label htmlFor="customerRating">Customer Rating</Label>
            <div className="flex space-x-2 mt-2">
              {[1, 2, 3, 4, 5].map((starValue) => (
                <button
                  key={starValue}
                  type="button"
                  className="focus:outline-none"
                  onClick={() => handleRatingChange(starValue)}
                >
                  <Star
                    className={`w-8 h-8 ${
                      starValue <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="paymentAmount">Payment Amount ({currencySymbol})</Label>
            <Input
              id="paymentAmount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className="mt-1 [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="Enter payment amount"
            />
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>

          <div>
            <Label htmlFor="comment">Comment (Optional)</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-1 min-h-[100px]"
              placeholder="Add any comments about this service"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={loading}>
            {loading ? "Processing..." : "Complete Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
